<template>
  <div class="file-view">
    <BannedView v-if="isBanned" :file-url="fileUrl" />

    <template v-else>
      <DownloadView
        v-if="isDownload"
        :file-url="fileUrl"
        :easy-file="easyFile"
        @download="clickDownloadFile"
      />

      <MiniProgramRedirect
        v-else-if="isMobile && isWebToMpRedirectEnable"
        @open-miniprogram="clickOpenMiniprogramBtn"
      />

      <FileViewer
        v-if="shouldRenderFileViewer"
        :file-url="fileUrl"
        :file-type="fileType"
        :easy-file="easyFile"
      />

      <IconReport class="report-icon" v-if="true" @click="clickReportIcon" />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, watchEffect } from "vue";
import { useEasyFile } from "@/composables/easyfile/useEasyFile";
import IconReport from "@/components/icons/IconReport.vue";
import BannedView from "./components/BannedView.vue";
import DownloadView from "./components/DownloadView.vue";
import MiniProgramRedirect from "./components/MiniProgramRedirect.vue";
import FileViewer from "./components/FileViewer.vue";
import Tracking from "@/model/tracking.js";
import { useInterstitialAd } from "@/composables/ads/useInterstitialAd";

const props = defineProps({
  relativeUrl: {
    type: String,
    required: true,
  },
});

const {
  state: {
    fileUrl,
    fileType,
    easyFile,
    isDownload,
    isMobile,
    isBanned,
    isWebToMpRedirectEnable,
  },
  action: { handleReport, handleOpenMiniProgram, handleDownload, initialize },
} = useEasyFile(props.relativeUrl);

const clickReportIcon = () => handleReport(ElMessageBox, ElMessage);

const clickOpenMiniprogramBtn = () => {
  Tracking.send({
    trigger: "button-click",
    page: "jump-to-mini-file-page",
    object: "jump-to-mini-btn",
  });
  handleOpenMiniProgram();
};

const clickDownloadFile = () => {
  Tracking.send({
    trigger: "button-click",
    page: "download-page",
    object: "download-btn",
  });
  handleDownload();
};

const shouldRenderFileViewer = computed(() => {
  return !isBanned.value && !isDownload.value && !(isMobile.value && isWebToMpRedirectEnable.value);
});

const { tryRenderInterstitialAd } = useInterstitialAd();
const adRenderedThisView = ref(false);

watchEffect(() => {
  if (shouldRenderFileViewer.value && !adRenderedThisView.value) {
    console.log("[EasyFileView] Viewer condition met and ad not rendered yet, calling tryRenderInterstitialAd...");
    const rendered = tryRenderInterstitialAd();
    if (rendered) {
      adRenderedThisView.value = true;
    }
  } else if (!shouldRenderFileViewer.value) {
      // 可选：如果需要，可以在这里处理 viewer 条件变为 false 的情况
      // console.log("[EasyFileView] Viewer condition became false.");
  }
});

watch(
  () => props.relativeUrl,
  (newUrl, oldUrl) => {
    if (newUrl !== oldUrl && oldUrl !== undefined) {
      console.log("[EasyFileView] View identifier changed, resetting ad render flag.");
      adRenderedThisView.value = false;
    }
  },
  { immediate: false }
);

onMounted(async () => {
  console.log("[EasyFileView] Mounted");
  await initialize();

  if (isDownload.value) {
    Tracking.send({
      trigger: "page-view",
      page: "download-page",
    });
  } else if (isMobile.value && isWebToMpRedirectEnable.value) {
    Tracking.send({
      trigger: "page-view",
      page: "jump-to-mini-file-page",
    });
  } else {
    Tracking.send({
      trigger: "page-view",
      page: "file-page",
    });
  }
});
</script>

<style scoped lang="scss">
.file-view {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

@media screen and (max-width: 768px) {
  .report-icon {
    position: fixed;
    top: 5.56vw;
    left: 5.56vw;
    height: 3.24vw;
    width: 3.55vw;
    cursor: pointer;
    opacity: 0.5;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 1;
    }
  }
}

@media screen and (min-width: 769px) {
  .report-icon {
    position: fixed;
    top: 2.78vh;
    left: 1.56vw;
    height: 1.6vh;
    width: 1.76vh;
    cursor: pointer;
    opacity: 0.5;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 1;
    }
  }
}
</style>
